import { useId, useState, type ReactNode } from 'react';
import type { ServiceKeyInfo, Tenant } from '../../../identity/api';
import { Chip } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { fmtDate } from '../../../identity/format';
import { SecretDialog } from '@nyce/admin-kit/console';
import {
  Code,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  TableScroller,
  useAsync,
} from '../../../components/common';
import { ScimConnection } from './ScimConnection';

type Notify = { success: (t: string) => void; error: (t: string) => void };

/** What differs between the two key kinds; the interaction (one active key, issue/rotate/revoke) is shared. */
interface KeyKind {
  title: string;
  /** Short noun used in button labels and the status chip ("" for the exchange key, whose labels predate this). */
  noun: string;
  testId: string;
  rowAttr: string;
  api: {
    list: (tenantId: string) => Promise<ServiceKeyInfo[]>;
    issue: (tenantId: string) => Promise<string>;
    revoke: (tenantId: string) => Promise<void>;
  };
  intro: ReactNode;
  revealTitle: string;
  revealHint: ReactNode;
  issueMessage: string;
  rotateMessage: string;
  revokeMessage: string;
  revokedNotice: string;
  emptyTitle: string;
  emptyHint: string;
}

const EXCHANGE: KeyKind = {
  title: 'Service keys',
  noun: '',
  testId: 'service-key',
  rowAttr: 'data-service-key',
  api: {
    list: (id) => client.tenants.serviceKeys.list(id),
    issue: async (id) => (await client.tenants.serviceKeys.issue(id)).service_key,
    revoke: (id) => client.tenants.serviceKeys.revoke(id),
  },
  intro: (
    <>
      Lets an upstream that already authenticated a user hand its JWT to <code>/api/auth/v1/exchange</code>{' '}
      with the raw key in <code>X-Service-Key</code>. Only the SHA-256 hash is stored.
    </>
  ),
  revealTitle: 'New service key',
  revealHint: (
    <>
      The caller sends it in the <code>X-Service-Key</code> header of <code>POST /api/auth/v1/exchange</code>.
    </>
  ),
  issueMessage: 'Issue a service key for this tenant?',
  rotateMessage:
    'Issue a new key? The current active key is revoked immediately; callers must switch to the new one.',
  revokeMessage: 'Revoke the active key? /exchange stops working for this tenant until a new key is issued.',
  revokedNotice: 'Service key revoked; /exchange is disabled for this tenant',
  emptyTitle: 'No service key issued.',
  emptyHint: 'Only needed when an upstream calls /exchange for this tenant.',
};

const PROVISIONING: KeyKind = {
  title: 'Provisioning key (SCIM)',
  noun: 'provisioning',
  testId: 'provisioning-key',
  rowAttr: 'data-provisioning-key',
  api: {
    list: (id) => client.tenants.provisioningKeys.list(id),
    issue: async (id) => (await client.tenants.provisioningKeys.issue(id)).provisioning_key,
    revoke: (id) => client.tenants.provisioningKeys.revoke(id),
  },
  intro: (
    <>
      The <b>Secret Token</b> of the SCIM connector (e.g. Entra ID provisioning) that creates users and groups
      in this tenant. Only works for <code>Database</code> tenants; separate from the service key.
    </>
  ),
  revealTitle: 'New provisioning key',
  revealHint: <>Paste it as the Secret Token in the SCIM connector.</>,
  issueMessage: 'Issue a SCIM provisioning key for this tenant?',
  rotateMessage:
    'Issue a new provisioning key? The current one is revoked immediately; the SCIM connector must be updated.',
  revokeMessage:
    'Revoke the provisioning key? SCIM provisioning stops for this tenant until a new key is issued.',
  revokedNotice: 'Provisioning key revoked; SCIM provisioning is disabled for this tenant',
  emptyTitle: 'No provisioning key issued.',
  emptyHint: 'Only needed when an identity provider provisions users over SCIM.',
};

function KeySection({
  kind,
  tenant,
  canWrite,
  notify,
}: {
  kind: KeyKind;
  tenant: Tenant;
  canWrite: boolean;
  notify: Notify;
}) {
  const keys = useAsync(() => kind.api.list(tenant.id), [kind, tenant.id]);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const active = (keys.data ?? []).find((k) => !k.revokedAt);
  const what = kind.noun ? `${kind.noun} key` : 'key';

  const issue = async () => {
    setBusy(true);
    try {
      setRevealed(await kind.api.issue(tenant.id));
      await keys.reload();
    } catch (e) {
      notify.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const revoke = async () => {
    try {
      await kind.api.revoke(tenant.id);
      await keys.reload();
      notify.success(kind.revokedNotice);
    } catch (e) {
      notify.error((e as Error).message);
    }
  };

  const headingId = useId();
  const rows = [...(keys.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return (
    <section className="nyk-card" aria-labelledby={headingId}>
      <div className="nyk-card-head">
        <div className="nyk-card-title-group">
          <h2 id={headingId}>{kind.title}</h2>
          <Chip size="sm" selected={!!active}>
            {active ? `active ${what}` : `no active ${what}`}
          </Chip>
        </div>
        {canWrite && (
          <div className="nyk-actions">
            <ConfirmButton
              size="sm"
              message={active ? kind.rotateMessage : kind.issueMessage}
              onConfirm={() => void issue()}
              loading={busy}
            >
              {active ? `Rotate ${what}` : `Issue ${what}`}
            </ConfirmButton>
            {active && (
              <ConfirmButton
                size="sm"
                variant="danger"
                message={kind.revokeMessage}
                onConfirm={() => void revoke()}
              >
                {kind.noun ? `Revoke ${what}` : 'Revoke'}
              </ConfirmButton>
            )}
          </div>
        )}
      </div>
      <p className="nyk-card-intro">{kind.intro}</p>
      <ErrorBox error={keys.error} />
      <Loading loading={keys.loading} />
      <TableScroller>
        <table className="nyk-ui-table nyk-cards" data-testid={`${kind.testId}s-table`}>
          <thead>
            <tr>
              <th>Key</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Revoked</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((k) => (
              <tr key={k.id} {...{ [kind.rowAttr]: k.id }}>
                <td className="nyk-card-title">
                  <Code>{k.id}</Code>
                </td>
                <td data-label="Status">
                  <Chip size="sm" selected={!k.revokedAt}>
                    {k.revokedAt ? 'revoked' : 'active'}
                  </Chip>
                </td>
                <td data-label="Created">{fmtDate(k.createdAt)}</td>
                <td data-label="Last used">{fmtDate(k.lastUsedAt)}</td>
                <td data-label="Revoked">{fmtDate(k.revokedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroller>
      {!keys.loading && !rows.length && <EmptyState title={kind.emptyTitle} hint={kind.emptyHint} />}
      {revealed && (
        <SecretDialog
          value={revealed}
          title={kind.revealTitle}
          data-testid={kind.testId}
          ackLabel="I have stored the key"
          onClose={() => setRevealed(null)}
        >
          {kind.revealHint}
        </SecretDialog>
      )}
    </section>
  );
}

/**
 * Per-tenant keys, one active key of each kind (issuing revokes the previous one):
 * - service keys authenticate an upstream that calls `/exchange` with a JWT it minted itself;
 * - the provisioning key authenticates the SCIM gateway that provisions users and groups.
 */
export function ServiceKeysTab({
  tenant,
  canWrite,
  notify,
}: {
  tenant: Tenant;
  canWrite: boolean;
  notify: Notify;
}) {
  const [round, setRound] = useState(0);
  return (
    <>
      <KeySection kind={EXCHANGE} tenant={tenant} canWrite={canWrite} notify={notify} />
      <KeySection key={round} kind={PROVISIONING} tenant={tenant} canWrite={canWrite} notify={notify} />
      <ScimConnection
        tenant={tenant}
        canWrite={canWrite}
        notify={notify}
        onKeyIssued={() => setRound((r) => r + 1)}
      />
    </>
  );
}
